import { RynReplayNoTokenReason } from './ryn-replay-access-token';

/**
 * Turns a read API refusal status into a sentence a support engineer can act on.
 *
 * This lives on its own because BOTH read surfaces need it, the events endpoint
 * and the sessions list, and they refuse for the same reasons with the same
 * statuses. Two copies would drift, and the way they would drift is the bad way:
 * someone improves the wording on one screen and the other screen keeps telling
 * people something less useful.
 *
 * Each status means something different and specific. Collapsing them into
 * "failed to load" throws away the only diagnosis the server offered.
 */
export const describeRynReplayReadFailure = (status: number): string => {
  switch (status) {
    case 401:
      return 'Your session has expired. Sign in again to view this recording.';
    case 403:
      return 'You do not have access to session replays. Ask an administrator to grant you the Ryn Replay player permission.';
    case 404:
      return 'No recording exists with that id.';
    case 503:
      return 'The replay service is temporarily unavailable. Try again shortly.';
    default:
      return `The recording could not be loaded (HTTP ${status}).`;
  }
};

/**
 * Same statuses, but worded for the LIST rather than one recording.
 *
 * The list fails for the same reasons and it would be tempting to reuse the
 * function above. Do not. "No recording exists with that id" is nonsense on a
 * screen that never named an id, and 503 on the list means you see nothing at
 * all rather than one thing being unavailable. Shared statuses do not mean
 * shared sentences.
 */
export const describeRynReplayListFailure = (status: number): string => {
  switch (status) {
    case 401:
      return 'Your session has expired. Sign in again to see recordings.';
    case 403:
      return 'You do not have access to session replays. Ask an administrator to grant you the Ryn Replay player permission.';
    case 503:
      return 'The replay service is temporarily unavailable, so recordings cannot be listed. Try again shortly.';
    default:
      return `Recordings could not be listed (HTTP ${status}).`;
  }
};

/**
 * Worded for the LIST when the read API was never called because there is no
 * usable Entra token. This is a different axis from the status functions above:
 * there a request WAS made and the server answered; here nothing was sent and
 * the reason comes from the token factory. #30126: the three no-token states
 * must not collapse into "you are not signed in", which told a signed-in Auth0
 * viewer their login was broken and offered no action that helps.
 */
export const describeRynReplayListNoToken = (
  reason: RynReplayNoTokenReason | undefined
): string => {
  switch (reason) {
    case 'wrong-issuer':
      return 'Recordings require a corporate (Entra) sign in. You are signed in with a UPS1 account, which cannot list recordings.';
    case 'acquire-failed':
      return 'We could not confirm your corporate (Entra) sign in, so recordings cannot be listed. Try again shortly.';
    case 'no-session':
    default:
      return 'You are not signed in. Sign in with a corporate (Entra) account to see recordings.';
  }
};

/**
 * Same three no-token reasons, worded for a single recording rather than the
 * list. Kept distinct from the list wording for the same reason the status
 * functions are: "list recordings" is nonsense on a screen showing one.
 */
export const describeRynReplayReadNoToken = (
  reason: RynReplayNoTokenReason | undefined
): string => {
  switch (reason) {
    case 'wrong-issuer':
      return 'Recordings require a corporate (Entra) sign in. You are signed in with a UPS1 account, which cannot view this recording.';
    case 'acquire-failed':
      return 'We could not confirm your corporate (Entra) sign in, so this recording cannot be loaded. Try again shortly.';
    case 'no-session':
    default:
      return 'You are not signed in. Sign in with a corporate (Entra) account to view this recording.';
  }
};
